import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit3, Trash2, Eye, Users, Mail, Phone,
  ChevronLeft, ChevronRight, X, GraduationCap, User as UserIcon, Save, Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from '../components/IconButton';
import ImportModal from '../components/ImportModal';

const initialForm = {
  nome_participa: '',
  email_participa: '',
  cel_participa: '',
  cpf_participa: '',
  id_curso: ''
};

// Máscaras
const formatCPFInput = (value) => {
  let v = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (v.length > 9) return v.replace(/(\d{3})(\d{3})(\d{3})(\d)/, '$1.$2.$3-$4');
  if (v.length > 6) return v.replace(/(\d{3})(\d{3})(\d)/, '$1.$2.$3');
  if (v.length > 3) return v.replace(/(\d{3})(\d)/, '$1.$2');
  return v;
};

const formatCelularInput = (value) => {
  let v = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (v.length > 6) return v.replace(/(\d{2})(\d{5})(\d)/, '($1) $2-$3');
  if (v.length > 2) return v.replace(/(\d{2})(\d)/, '($1) $2');
  return v.length ? `(${v}` : '';
};

const formatCPFDisplay = (cpf) => {
  if (!cpf) return '-';
  const cleaned = String(cpf).replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatCelularDisplay = (cel) => {
  if (!cel) return '-';
  const cleaned = String(cel).replace(/\D/g, '');
  if (cleaned.length === 11) return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (cleaned.length === 10) return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return cel;
};

// Validações
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidCPF = (cpf) => {
  const c = String(cpf || '').replace(/\D/g, '');
  if (c.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(c)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(c[i], 10) * (10 - i);
  let d1 = 11 - (sum % 11);
  if (d1 >= 10) d1 = 0;
  if (parseInt(c[9], 10) !== d1) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(c[i], 10) * (11 - i);
  let d2 = 11 - (sum % 11);
  if (d2 >= 10) d2 = 0;
  return parseInt(c[10], 10) === d2;
};

const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm";

const Field = ({ label, required, children, error }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const Participantes = () => {
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewingParticipante, setViewingParticipante] = useState(null);
  const [editingParticipante, setEditingParticipante] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [cursos, setCursos] = useState([]);

  // Paginação
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  useEffect(() => { loadParticipantes(); }, [page]);
  useEffect(() => { loadCursos(); }, []);
  useEffect(() => { document.title = 'Participantes - EstOX'; }, []);

  const loadCursos = async () => {
    try {
      const res = await api.get('/cursos', { params: { limit: 100 } });
      setCursos(res.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
    }
  };

  const loadParticipantes = async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await api.get('/participantes', { params });
      setParticipantes(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar participantes:', error);
      toast.error('Erro ao carregar participantes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadParticipantes();
  };

  const openModal = (participante = null) => {
    setEditingParticipante(participante);
    setViewingParticipante(null);
    setFormErrors({});
    setFormData({
      nome_participa: participante?.nome_participa || '',
      email_participa: participante?.email_participa || '',
      cel_participa: participante?.cel_participa ? formatCelularInput(participante.cel_participa) : '',
      cpf_participa: participante?.cpf_participa ? formatCPFInput(participante.cpf_participa) : '',
      id_curso: participante?.id_curso ? String(participante.id_curso) : ''
    });
    setShowModal(true);
  };

  const openView = (participante) => {
    setViewingParticipante(participante);
    setEditingParticipante(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingParticipante(null);
    setViewingParticipante(null);
    setFormData(initialForm);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (formData.nome_participa && formData.nome_participa.length > 80) {
      errors.nome_participa = 'Nome deve ter no máximo 80 caracteres';
    }

    if (formData.email_participa && !isValidEmail(formData.email_participa)) {
      errors.email_participa = 'E-mail inválido';
    }

    if (formData.cel_participa) {
      const digits = formData.cel_participa.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 11) {
        errors.cel_participa = 'Celular inválido (use DDD + número)';
      }
    }

    if (formData.cpf_participa && !isValidCPF(formData.cpf_participa)) {
      errors.cpf_participa = 'CPF inválido';
    }

    if (!formData.id_curso) {
      errors.id_curso = 'Selecione o curso';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = {
        nome_participa: formData.nome_participa.trim() || null,
        email_participa: formData.email_participa.trim() || null,
        cel_participa: formData.cel_participa ? formData.cel_participa.replace(/\D/g, '') : null,
        cpf_participa: formData.cpf_participa ? formData.cpf_participa.replace(/\D/g, '') : null,
        id_curso: formData.id_curso ? Number(formData.id_curso) : null
      };

      if (editingParticipante) {
        await api.put(`/participantes/${editingParticipante.id_participa}`, payload);
        toast.success('Participante atualizado com sucesso!');
      } else {
        await api.post('/participantes', payload);
        toast.success('Participante criado com sucesso!');
      }
      closeModal();
      loadParticipantes();
    } catch (error) {
      console.error('Erro ao salvar participante:', error);
      const msg = error?.response?.data?.message || 'Erro ao salvar participante';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/participantes/${deleteTarget.id_participa}`);
      toast.success('Participante excluído com sucesso!');
      setDeleteTarget(null);
      loadParticipantes();
    } catch (error) {
      console.error('Erro ao excluir participante:', error);
      toast.error('Erro ao excluir participante');
    }
  };

  // Campos disponíveis para importação via planilha
  const importFields = [
    { key: 'nome_participa', label: 'Nome', required: true },
    { key: 'email_participa', label: 'E-mail', required: false },
    { key: 'cel_participa', label: 'Celular', required: false },
    { key: 'cpf_participa', label: 'CPF', required: false }
  ];

  // Curso é obrigatório e não vem da planilha (aplica-se a todos os registros)
  const importExtraFields = [
    {
      key: 'id_curso',
      label: 'Curso',
      required: true,
      options: cursos.map(c => ({ value: String(c.id_curso), label: c.descr_curso }))
    }
  ];

  const handleImportData = async (data, extraValues = {}) => {
    const id_curso = extraValues.id_curso ? Number(extraValues.id_curso) : null;
    if (!id_curso) {
      toast.error('Selecione o curso antes de importar');
      return;
    }
    setShowImportModal(false);
    const toastId = toast.loading('Importando participantes...');
    let successCount = 0;
    let errorCount = 0;
    let firstError = null;

    try {
      for (const row of data) {
        const payload = {
          nome_participa: row.nome_participa,
          email_participa: row.email_participa,
          cel_participa: row.cel_participa,
          cpf_participa: row.cpf_participa,
          id_curso
        };

        // Limpa valores vazios (o backend trata null para campos opcionais)
        Object.keys(payload).forEach(key => {
          if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
            delete payload[key];
          }
        });

        try {
          await api.post('/participantes', payload);
          successCount++;
        } catch (err) {
          console.error('Erro importando linha', row, err);
          errorCount++;
          if (!firstError) firstError = err?.response?.data?.message || err.message;
        }
      }

      toast.dismiss(toastId);
      if (errorCount === 0) {
        toast.success(`${successCount} participante(s) importado(s) com sucesso!`);
      } else {
        toast.error(`${successCount} importado(s). ${errorCount} falha(s). Primeiro erro: ${firstError || 'Desconhecido'}`);
      }
      loadParticipantes();
    } catch (err) {
      console.error(err);
      toast.dismiss(toastId);
      toast.error('Erro geral: ' + (err.message || String(err)));
    }
  };

  const isViewing = !!viewingParticipante;
  const displayParticipante = viewingParticipante || editingParticipante;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Participantes</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerencie os participantes dos cursos de capacitação</p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            icon={Upload}
            onClick={() => setShowImportModal(true)}
            variant="success"
            title="Importar"
          />
          <IconButton
            icon={Plus}
            onClick={() => openModal()}
            variant="primary"
            title="Novo Participante"
          />
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total de participantes</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{total}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Com e-mail</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {participantes.filter(p => p.email_participa).length}
            </p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Com celular</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {participantes.filter(p => p.cel_participa).length}
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, e-mail, celular ou CPF..."
              className={`${inputClass} pl-10`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <IconButton icon={Search} onClick={handleSearch} variant="primary" title="Buscar" />
        </div>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Curso</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Celular</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : participantes.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <Users className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Nenhum participante encontrado</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {searchTerm ? 'Tente ajustar a busca.' : 'Comece cadastrando um novo participante.'}
                    </p>
                  </td>
                </tr>
              ) : (
                participantes.map((p) => (
                  <tr key={p.id_participa} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">#{p.id_participa}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{p.nome_participa || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{p.descr_curso || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{formatCelularDisplay(p.cel_participa)}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <IconButton icon={Eye} onClick={() => openView(p)} variant="info" size="sm" title="Visualizar" />
                        <IconButton icon={Edit3} onClick={() => openModal(p)} variant="warning" size="sm" title="Editar" />
                        <IconButton icon={Trash2} onClick={() => setDeleteTarget(p)} variant="danger" size="sm" title="Excluir" />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Página {page} de {totalPages} · {total} registro(s)
            </p>
            <div className="flex items-center gap-2">
              <IconButton icon={ChevronLeft} onClick={() => setPage(p => Math.max(1, p - 1))} variant="secondary" size="sm" title="Anterior" disabled={page <= 1} />
              <IconButton icon={ChevronRight} onClick={() => setPage(p => Math.min(totalPages, p + 1))} variant="secondary" size="sm" title="Próximo" disabled={page >= totalPages} />
            </div>
          </div>
        )}
      </div>

      {/* Modal de formulário / visualização */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
          <div className="relative top-10 w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            {/* Cabeçalho do modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isViewing ? 'Detalhes do Participante' : editingParticipante ? 'Editar Participante' : 'Novo Participante'}
                </h3>
              </div>
              <IconButton icon={X} onClick={closeModal} variant="secondary" size="sm" title="Fechar" />
            </div>

            {/* Corpo do modal */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {isViewing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Nome completo</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{displayParticipante?.nome_participa || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Curso</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">{displayParticipante?.descr_curso || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">E-mail</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">{displayParticipante?.email_participa || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Celular</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">{formatCelularDisplay(displayParticipante?.cel_participa)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">CPF</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">{formatCPFDisplay(displayParticipante?.cpf_participa)}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSave} className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <UserIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Dados do participante</h4>
                    </div>
                    <Field label="Curso" required error={formErrors.id_curso}>
                      <select
                        className={inputClass}
                        value={formData.id_curso}
                        onChange={(e) => setFormData({ ...formData, id_curso: e.target.value })}
                      >
                        <option value="">Selecione o curso</option>
                        {cursos.map(c => (
                          <option key={c.id_curso} value={c.id_curso}>{c.descr_curso}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Nome completo" error={formErrors.nome_participa}>
                      <input
                        type="text"
                        maxLength={80}
                        className={inputClass}
                        value={formData.nome_participa}
                        onChange={(e) => setFormData({ ...formData, nome_participa: e.target.value })}
                        placeholder="Nome completo do participante"
                      />
                    </Field>
                    <Field label="E-mail" error={formErrors.email_participa}>
                      <input
                        type="email"
                        maxLength={80}
                        className={inputClass}
                        value={formData.email_participa}
                        onChange={(e) => setFormData({ ...formData, email_participa: e.target.value })}
                        placeholder="participante@email.com"
                      />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Celular" error={formErrors.cel_participa}>
                        <input
                          type="text"
                          className={inputClass}
                          value={formData.cel_participa}
                          onChange={(e) => setFormData({ ...formData, cel_participa: formatCelularInput(e.target.value) })}
                          placeholder="(00) 00000-0000"
                          maxLength={15}
                        />
                      </Field>
                      <Field label="CPF" error={formErrors.cpf_participa}>
                        <input
                          type="text"
                          className={inputClass}
                          value={formData.cpf_participa}
                          onChange={(e) => setFormData({ ...formData, cpf_participa: formatCPFInput(e.target.value) })}
                          placeholder="000.000.000-00"
                          maxLength={14}
                        />
                      </Field>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                    <IconButton icon={Save} type="submit" disabled={isSaving} variant="primary" title={isSaving ? 'Salvando...' : editingParticipante ? 'Atualizar' : 'Salvar'} />
                  </div>
                </form>
              )}
            </div>

            {/* Rodapé do modal (visualização) */}
            {isViewing && (
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <IconButton icon={X} onClick={closeModal} variant="secondary" title="Fechar" />
                <IconButton icon={Edit3} onClick={() => openModal(displayParticipante)} variant="warning" title="Editar" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Excluir participante</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir o participante <strong>"{deleteTarget.nome_participa || '#' + deleteTarget.id_participa}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <IconButton icon={X} onClick={() => setDeleteTarget(null)} variant="secondary" title="Cancelar" />
              <IconButton icon={Trash2} onClick={confirmDelete} variant="danger" title="Excluir" />
            </div>
          </div>
        </div>
      )}

      {/* Modal de importação de participantes */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportData}
        fields={importFields}
        extraFields={importExtraFields}
      />
    </div>
  );
};

export default Participantes;
